import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  saveStop,
  setStopCollapsed,
  useConfig,
  type SavedStop,
} from '../lib/config.ts';
import { useDepartures } from '../lib/queries.ts';
import { clockFor, countdown, useClockTick } from '../lib/time.ts';
import { hasPassed } from '../lib/cache.ts';
import {
  FORECAST_MINUTES,
  isCancelled,
  SlError,
  matches,
  minutesLate,
  notices,
  pairKey,
  type Departure,
  type Pair,
} from '../lib/sl.ts';
import {
  Button,
  Card,
  Disclosure,
  Empty,
  LineBadge,
  Notice,
  Skeleton,
} from '../components/ui.tsx';

const cx = (...parts: (string | false | null | undefined)[]) =>
  parts.filter(Boolean).join(' ');

/** How many departures to show per line. SL returns at most three anyway. */
const PER_PAIR = 3;

/** Varied a little, so the placeholder reads as times rather than a bar chart. */
const SKELETON_WIDTHS = ['w-8', 'w-10', 'w-10'];

/** "Inget inom 3 h" -- kept in step with the window actually requested. */
const WINDOW_LABEL = `Inget inom ${Math.round(FORECAST_MINUTES / 60)} h`;

export function Board() {
  const { stops } = useConfig();

  return (
    <>
      <header className="mb-4 flex items-baseline justify-between gap-3">
        <h1 className="text-2xl">Avgångar</h1>
        <Link
          to="/setup"
          className="text-accent focus-visible:outline-accent rounded-lg text-[15px] font-semibold focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          Hållplatser
        </Link>
      </header>

      {stops.length === 0 ? (
        <Card className="p-4">
          <Empty title="Inga hållplatser än">
            Välj hållplatser och vilka linjer du vill se.
          </Empty>
          <Link to="/setup" className="block">
            <Button className="w-full">Lägg till hållplats</Button>
          </Link>
        </Card>
      ) : (
        <div className="space-y-3">
          {stops.map((stop) => (
            <StopCard key={stop.siteId} stop={stop} />
          ))}
        </div>
      )}
    </>
  );
}

function StopCard({ stop }: { stop: SavedStop }) {
  const collapsed = Boolean(stop.collapsed);
  // Re-render the card every few seconds, so countdowns stay true between the
  // now-infrequent refreshes and a departure that has gone drops off rather
  // than sitting at "Nu" until the next one arrives.
  useClockTick();
  const q = useDepartures(stop.siteId, stop.pairs, !collapsed);
  useLearnStands(stop, q.data?.departures);

  return (
    <Card>
      <Disclosure
        open={!collapsed}
        onOpenChange={(open) => setStopCollapsed(stop.siteId, !open)}
        summaryClassName="border-line items-baseline px-4 py-3 group-open:border-b"
        summary={
          <>
            <h2 className="min-w-0 flex-1 truncate text-[17px]">
              {stop.name}
              {(stop.note ?? stop.area) && (
                <span className="text-faint ml-1.5 text-[13px] font-normal">
                  {[stop.note, stop.area].filter(Boolean).join(' · ')}
                </span>
              )}
            </h2>
            {/* Only while shut, so a collapsed stop still says what is inside
                it. Hidden with CSS rather than a second render path. */}
            <span className="text-faint shrink-0 text-[13px] group-open:hidden">
              {stop.pairs.length} {stop.pairs.length === 1 ? 'linje' : 'linjer'}
            </span>
            <Freshness
              isFetching={q.isFetching}
              error={q.error}
              hasData={Boolean(q.data)}
              updatedAt={q.dataUpdatedAt}
            />
          </>
        }
      >
        {stop.pairs.length === 0 ? (
          <p className="text-faint px-4 py-4 text-[13px]">
            Inga linjer valda för den här hållplatsen.
          </p>
        ) : (
          <ul className="divide-line divide-y">
            {stop.pairs.flatMap((pair) =>
              rowsFor(pair, q.data?.departures).map((row) => (
                <PairRow
                  key={row.key}
                  row={row}
                  // Only an error with nothing cached is worth saying out loud
                  // on the row; a failed refresh that still has times shows the
                  // last ones with a stale marker in the header instead.
                  failed={q.isError && !q.data}
                  loading={q.isPending}
                />
              )),
            )}
          </ul>
        )}

        {/* Stop-wide notices: lifts out of service, a closed entrance, trains
            starting from somewhere else. Shown once per stop rather than
            repeated on every row they touch. */}
        {q.data && q.data.stop_deviations.length > 0 && (
          <div className="border-line space-y-1 border-t px-4 py-2.5">
            {q.data.stop_deviations.map((d) => (
              <Notice key={d.id}>{d.message}</Notice>
            ))}
          </div>
        )}
      </Disclosure>
    </Card>
  );
}

type Row = {
  key: string;
  line: string;
  mode: string;
  label: string;
  /** Live where available, falling back to what was saved. */
  stand: string | undefined;
  departures: Departure[];
};

/**
 * What one saved pair becomes on screen.
 *
 * Usually one row. A pair added by hand has no direction -- it matches the
 * line number alone -- so it becomes one row per direction the line is
 * actually running, each labelled with its real destination. That turns
 * "176, alla riktningar" into the two proper rows the picker would have
 * offered, the moment SL has a departure to learn them from.
 */
function rowsFor(pair: Pair, departures: Departure[] | undefined): Row[] {
  // Anything already gone is dropped here rather than rendered as "Nu"
  // forever. It matters most for times restored from cache, which can be
  // half an hour old, but it is right for live answers too -- SL lists a
  // departure a few minutes past as still being at the stop.
  const mine = (departures ?? []).filter((d) => matches(d, pair) && !hasPassed(d));

  // The mode is taken from a live departure where there is one: a hand-added
  // line is assumed to be a bus, and this is what quietly corrects the badge
  // colour when it turns out to be a tram.
  const mode = mine[0]?.line.transport_mode ?? pair.mode;

  if (pair.directionCode !== null) {
    return [
      {
        key: pairKey(pair),
        line: pair.line,
        mode,
        label: pair.direction,
        stand: mine[0]?.stop_point?.designation ?? pair.stand,
        departures: mine.slice(0, PER_PAIR),
      },
    ];
  }

  const byDirection = new Map<number, Departure[]>();
  for (const d of mine) {
    const list = byDirection.get(d.direction_code);
    if (list) list.push(d);
    else byDirection.set(d.direction_code, [d]);
  }

  // Nothing running: keep a single placeholder row so the line does not
  // vanish from the board entirely.
  if (byDirection.size === 0) {
    return [
      {
        key: pairKey(pair),
        line: pair.line,
        mode: pair.mode,
        label: pair.direction,
        stand: pair.stand,
        departures: [],
      },
    ];
  }

  return [...byDirection.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([code, list]) => ({
      key: `${pairKey(pair)}:${code}`,
      line: pair.line,
      mode: list[0]?.line.transport_mode ?? pair.mode,
      label: list[0]?.destination ?? pair.direction,
      stand: list[0]?.stop_point?.designation ?? pair.stand,
      departures: list.slice(0, PER_PAIR),
    }));
}

/**
 * Keep each saved pair's stand in step with reality.
 *
 * The stand is stored so a row can be drawn at its final height before any
 * request finishes. That only works if the stored value is right, so it is
 * reconciled whenever fresh departures arrive: stops saved before stands were
 * stored fill themselves in on first view, and a line moved to a different
 * stand corrects itself on the next load.
 *
 * Pairs with no departures right now keep whatever they had -- their stand is
 * unknown at this moment, not absent, and blanking it would reintroduce
 * exactly the layout shift this exists to prevent. Writing only on a real
 * difference is what stops this from looping: the write re-renders the card,
 * the values then agree, and it settles.
 */
function useLearnStands(stop: SavedStop, departures: Departure[] | undefined) {
  useEffect(() => {
    if (!departures?.length) return;

    let changed = false;
    const pairs = stop.pairs.map((pair) => {
      const live = departures.find((d) => matches(d, pair))?.stop_point?.designation;
      if (!live || live === pair.stand) return pair;
      changed = true;
      return { ...pair, stand: live };
    });

    if (changed) saveStop({ ...stop, pairs });
  }, [stop, departures]);
}

function PairRow({
  row,
  failed,
  loading,
}: {
  row: Row;
  failed: boolean;
  loading: boolean;
}) {
  const mine = row.departures;

  // Any non-cancellation notice touching this line, deduplicated -- the same
  // engineering-works message is attached to every affected departure.
  const messages = [...new Set(mine.flatMap((d) => notices(d).map((n) => n.message)))];

  // The emphasised time is the next one you can actually catch. A cancelled
  // departure keeps its place in the row -- dropping it would silently turn
  // three times into two -- but it must not be the big number, or the row
  // leads with a service that is not running.
  const lead = mine.findIndex((d) => !isCancelled(d));

  // Lateness is reported for that same departure, since it is the one the
  // decision is about. One minute is noise on a bus; two is a decision.
  const leadLate = lead === -1 ? 0 : minutesLate(mine[lead] as Departure);

  // Said plainly rather than left to the strike-through, which shows that a
  // departure is cancelled but never that one was.
  const cancelledCount = mine.filter(isCancelled).length;

  return (
    <li className="px-4 py-3">
      <div className="flex items-center gap-3">
        <LineBadge line={row.line} mode={row.mode} />

        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-medium">{row.label}</p>
          {row.stand && (
            <p className="text-faint text-[13px]">
              {standWord(row.mode)} {row.stand}
            </p>
          )}
        </div>

        {/*
          Three fixed columns rather than a row that hugs its contents.
          Right-aligning the group as a whole still leaves every row starting
          its times at a different x -- "Nu 6 min 11 min" is far narrower than
          "14 min 17 min 26 min" -- so the times never line up into columns
          you can read down. 3.75rem is the widest a time gets: "18 min" at
          the lead size measures 56.6px.
        */}
        <div className="grid shrink-0 grid-cols-[repeat(3,3.75rem)] items-baseline">
          {loading ? (
            /*
              Each bar sits inside a span carrying the same font size as the
              time that will replace it, so the cell has the same line box and
              the same baseline. A bare 12px bar would make this whole block
              12px tall instead of 25.5px -- invisible on a row tall enough to
              show a stand, but a real collapse on the many stops that have
              none, where the times are the tallest thing in the row.
            */
            SKELETON_WIDTHS.map((width, i) => (
              <span
                key={`skeleton-${i}`}
                className={cx('text-right', i === 0 ? 'text-[17px]' : 'text-[15px]')}
              >
                <Skeleton className={cx('inline-block align-middle', width)} />
              </span>
            ))
          ) : failed ? (
            <TimesNote>—</TimesNote>
          ) : mine.length === 0 ? (
            <TimesNote>{WINDOW_LABEL}</TimesNote>
          ) : (
            /*
              Filled from the left, so a line with only one departure left in
              the hour puts it in the first column -- under the next
              departure of every other row. Reading straight down that column
              answers "what is leaving soonest", which is the question the
              board exists for. Right-aligning the short rows instead would
              tidy the card edge and move that answer to a different column
              on every row.
            */
            mine.map((d, i) => (
              <Time key={`${d.journey?.id ?? i}`} d={d} first={i === lead} />
            ))
          )}
        </div>
      </div>

      {/* Indented past the badge so the warnings line up with the
          destination they belong to rather than the line number. */}
      {(leadLate >= 2 || cancelledCount > 0 || messages.length > 0) && (
        <div className="mt-2 space-y-1 pl-14">
          {leadLate >= 2 && <Notice tone="late">{leadLate} min försenad</Notice>}
          {cancelledCount > 0 && (
            <Notice tone="late">
              {cancelledCount === 1
                ? 'En avgång är inställd'
                : `${cancelledCount} avgångar är inställda`}
            </Notice>
          )}
          {messages.map((m) => (
            <Notice key={m}>{m}</Notice>
          ))}
        </div>
      )}
    </li>
  );
}

/**
 * Buses and trams pull into a numbered stand -- "läge" on the sign at the
 * stop. Trains and the metro use track numbers, signed "spår". Using the
 * wrong word sends you looking for the wrong thing.
 */
const standWord = (mode: string) =>
  mode === 'TRAIN' || mode === 'METRO' ? 'Spår' : 'Läge';

/**
 * Something other than times, in the space times occupy.
 *
 * The outer span is sized like the lead time purely for its strut, so this
 * block keeps the same 25.5px line box that three real departures would
 * produce. Setting the small text directly on the grid cell instead would
 * make the block 18px, and a line that goes from "nothing in 3 h" to having
 * departures would jog the row -- the same collapse the skeletons had.
 */
function TimesNote({ children }: { children: ReactNode }) {
  return (
    <span className="col-span-3 text-right text-[17px]">
      <span className="text-faint align-middle text-[13px]">{children}</span>
    </span>
  );
}

function Time({ d, first }: { d: Departure; first: boolean }) {
  const cancelled = isCancelled(d);
  // One minute of lateness is noise on a bus. Two is a decision.
  const late = !cancelled && minutesLate(d) >= 2;

  // Computed here rather than taken from SL's own `display` field, which is
  // only true at the moment it was fetched. That is what lets the data behind
  // this refresh every couple of minutes while the text stays right.
  //
  // A cancelled departure shows the time it was meant to leave: counting down
  // to a service that is not running would be absurd, and SL's own board does
  // the same.
  // SL's own string is kept as a fallback for a departure that somehow
  // arrives without a usable timestamp, so a row degrades to stale text
  // rather than to nothing.
  const label = (cancelled ? clockFor(d.expected) : countdown(d.expected)) || d.display;

  return (
    <span
      className={cx(
        'nums text-right',
        first ? 'text-[17px] font-semibold' : 'text-[15px]',
        cancelled && 'text-danger line-through',
        late && 'text-late',
        !cancelled && !late && !first && 'text-muted',
      )}
      title={
        cancelled ? 'Inställd' : late ? `${minutesLate(d)} min försenad` : undefined
      }
    >
      {label}
    </span>
  );
}

/**
 * Whether what you are looking at is current.
 *
 * Silent while things are normal -- a board that is up to date should not
 * decorate itself -- and, importantly, silent through a failure it has
 * already recovered from. A single refresh failing while thirty-second-old
 * times are on screen is not worth an alarm; those times are still the right
 * answer. What matters is whether the numbers have gone stale, so that is
 * what it reports, in the warning colour once a failure is the reason.
 *
 * "Ingen kontakt" is reserved for having nothing to show at all, and a quota
 * refusal says so in its own words: nothing is broken, SL is just rationing,
 * and the only cure is waiting.
 */
function Freshness({
  isFetching,
  error,
  hasData,
  updatedAt,
}: {
  isFetching: boolean;
  error: unknown;
  hasData: boolean;
  updatedAt: number;
}) {
  if (isFetching) {
    return (
      <span
        aria-label="Uppdaterar"
        className="border-line border-t-accent size-3.5 shrink-0 animate-spin rounded-full border-2"
      />
    );
  }

  if (error && !hasData) {
    const quota = error instanceof SlError && error.isQuota;
    return (
      <span className="text-danger shrink-0 text-[13px]">
        {quota ? 'Väntar på SL' : 'Ingen kontakt'}
      </span>
    );
  }

  const age = updatedAt ? Date.now() - updatedAt : 0;
  // Quiet while things are fine and recent. But once a refresh has failed,
  // the age is said however young it is: what is on screen is then the last
  // thing SL told us rather than the current state, and that is worth knowing
  // even when it is only a minute old.
  if (age < 90_000 && !error) return null;

  return (
    <span
      className={cx('nums shrink-0 text-[13px]', error ? 'text-late' : 'text-faint')}
    >
      {Math.max(1, Math.round(age / 60_000))} min gammal
    </span>
  );
}
